import React, { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";
import { toast } from "sonner";
import {
  hasDeferredPrompt,
  isIOSSafari,
  isStandalone,
  onInstallStateChange,
  setInstallDecision,
  triggerNativeInstall,
} from "@/lib/install";
import { useLang } from "@/contexts/LangContext";

const COPY = {
  en: {
    label: "Install app",
    installed: "Installed. Beautiful, koumbare.",
    dismissed: "You can install any time from your browser menu.",
    ios: {
      title: "Add Eli & Mag to your home screen",
      body: "Tap the Share icon in Safari, then choose Add to Home Screen. That way Andreas can reach you.",
      step1: "Tap the Share icon",
      step1sub: "at the bottom of Safari",
      step2: "Choose \u201cAdd to Home Screen\u201d",
      step2sub: "then tap Add \u2014 done",
      gotIt: "Got it",
    },
  },
  fr: {
    label: "Installer l\u2019app",
    installed: "Install\u00e9. Magnifique, koumbare.",
    dismissed: "Tu peux installer plus tard depuis le menu du navigateur.",
    ios: {
      title: "Ajouter Eli & Mag \u00e0 l\u2019\u00e9cran d\u2019accueil",
      body: "Touche l\u2019ic\u00f4ne Partager dans Safari, puis choisis \u00ab Sur l\u2019\u00e9cran d\u2019accueil \u00bb. Andreas pourra te joindre.",
      step1: "Touche l\u2019ic\u00f4ne Partager",
      step1sub: "en bas de Safari",
      step2: "Choisis \u00ab Sur l\u2019\u00e9cran d\u2019accueil \u00bb",
      step2sub: "puis touche Ajouter",
      gotIt: "Compris",
    },
  },
  el: {
    label: "\u0395\u03b3\u03ba\u03b1\u03c4\u03ac\u03c3\u03c4\u03b1\u03c3\u03b7",
    installed: "\u0395\u03b3\u03ba\u03b1\u03c4\u03b1\u03c3\u03c4\u03ac\u03b8\u03b7\u03ba\u03b5. \u03a4\u03ad\u03bb\u03b5\u03b9\u03bf, \u03ba\u03bf\u03c5\u03bc\u03c0\u03ac\u03c1\u03b5.",
    dismissed: "\u039c\u03c0\u03bf\u03c1\u03b5\u03af\u03c2 \u03bd\u03b1 \u03b5\u03b3\u03ba\u03b1\u03c4\u03b1\u03c3\u03c4\u03ae\u03c3\u03b5\u03b9\u03c2 \u03b1\u03bd\u03ac \u03c0\u03ac\u03c3\u03b1 \u03c3\u03c4\u03b9\u03b3\u03bc\u03ae \u03b1\u03c0\u03cc \u03c4\u03bf \u03bc\u03b5\u03bd\u03bf\u03cd \u03c4\u03bf\u03c5 browser.",
    ios: {
      title: "\u03a0\u03c1\u03cc\u03c3\u03b8\u03b5\u03c3\u03b5 \u03c4\u03bf Eli & Mag \u03c3\u03c4\u03b7\u03bd \u03b1\u03c1\u03c7\u03b9\u03ba\u03ae \u03bf\u03b8\u03cc\u03bd\u03b7",
      body: "\u03a0\u03ac\u03c4\u03b1 \u03c4\u03bf \u03b5\u03b9\u03ba\u03bf\u03bd\u03af\u03b4\u03b9\u03bf \u039a\u03bf\u03b9\u03bd\u03bf\u03c0\u03bf\u03af\u03b7\u03c3\u03b7 \u03c3\u03c4\u03bf Safari, \u03bc\u03b5\u03c4\u03ac \u00ab\u03a0\u03c1\u03bf\u03c3\u03b8\u03ae\u03ba\u03b7 \u03c3\u03c4\u03b7\u03bd \u03b1\u03c1\u03c7\u03b9\u03ba\u03ae \u03bf\u03b8\u03cc\u03bd\u03b7\u00bb. \u0388\u03c4\u03c3\u03b9 \u03b8\u03b1 \u03c3\u03b5 \u03b2\u03c1\u03af\u03c3\u03ba\u03b5\u03b9 \u03bf Andreas.",
      step1: "\u03a0\u03ac\u03c4\u03b1 \u03c4\u03bf \u03b5\u03b9\u03ba\u03bf\u03bd\u03af\u03b4\u03b9\u03bf \u039a\u03bf\u03b9\u03bd\u03bf\u03c0\u03bf\u03af\u03b7\u03c3\u03b7",
      step1sub: "\u03c3\u03c4\u03bf \u03ba\u03ac\u03c4\u03c9 \u03bc\u03ad\u03c1\u03bf\u03c2 \u03c4\u03bf\u03c5 Safari",
      step2: "\u0394\u03b9\u03ac\u03bb\u03b5\u03be\u03b5 \u00ab\u03a0\u03c1\u03bf\u03c3\u03b8\u03ae\u03ba\u03b7 \u03c3\u03c4\u03b7\u03bd \u03b1\u03c1\u03c7\u03b9\u03ba\u03ae\u00bb",
      step2sub: "\u03bc\u03b5\u03c4\u03ac \u03c0\u03ac\u03c4\u03b1 \u03a0\u03c1\u03bf\u03c3\u03b8\u03ae\u03ba\u03b7",
      gotIt: "\u039a\u03b1\u03c4\u03ac\u03bb\u03b1\u03b2\u03b1",
    },
  },
  pl: {
    label: "Zainstaluj",
    installed: "Zainstalowane. Pi\u0119knie, kumie.",
    dismissed: "Mo\u017cesz zainstalowa\u0107 p\u00f3\u017aniej z menu przegl\u0105darki.",
    ios: {
      title: "Dodaj Eli & Mag do ekranu g\u0142\u00f3wnego",
      body: "Dotknij ikony Udost\u0119pnij w Safari, potem wybierz \u00abDo ekranu g\u0142\u00f3wnego\u00bb. Tak Andreas do ciebie dotrze.",
      step1: "Dotknij ikony Udost\u0119pnij",
      step1sub: "na dole Safari",
      step2: "Wybierz \u00abDo ekranu g\u0142\u00f3wnego\u00bb",
      step2sub: "potem stuknij Dodaj",
      gotIt: "Jasne",
    },
  },
};

const GuestInstallChip = ({ variant = "desktop", onNavigate }) => {
  const { lang } = useLang();
  const [tick, setTick] = useState(0);
  const [showIos, setShowIos] = useState(false);
  useEffect(() => onInstallStateChange(() => setTick((n) => n + 1)), []);

  const c = COPY[lang] || COPY.en;

  if (isStandalone()) return null;
  const iOS = isIOSSafari();
  const canPrompt = hasDeferredPrompt();
  if (!iOS && !canPrompt) return null;

  const onClick = async () => {
    if (iOS) {
      setShowIos(true);
      if (typeof onNavigate === "function") onNavigate();
      return;
    }
    const res = await triggerNativeInstall();
    if (res.ok) {
      toast.success(c.installed);
    } else if (res.outcome === "dismissed") {
      toast(c.dismissed);
    } else {
      // Deferred prompt not yet available for some reason – show tutorial as fallback.
      setShowIos(true);
    }
    if (typeof onNavigate === "function") onNavigate();
  };

  const btnClass =
    variant === "mobile"
      ? "w-full inline-flex items-center justify-center gap-2 h-9 px-4 rounded-full border border-accent/40 text-accent hover:border-accent hover:bg-accent/10 transition-colors text-[11px] tracking-[0.16em] uppercase"
      : variant === "mini"
      ? "inline-flex items-center justify-center w-9 h-9 rounded-full border border-accent/40 text-accent hover:border-accent hover:bg-accent/10 transition-colors"
      : "inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-accent/40 text-accent hover:border-accent hover:bg-accent/10 transition-colors text-[11px] tracking-[0.18em] uppercase";

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className={btnClass}
        data-testid={`guest-install-chip-${variant}`}
        title={c.label}
        aria-label={c.label}
      >
        <Download className={variant === "mobile" || variant === "mini" ? "w-4 h-4" : "w-3.5 h-3.5"} />
        {variant !== "mini" && <span>{c.label}</span>}
      </button>

      {showIos && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-foreground/45 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          data-testid="guest-install-ios"
          onClick={() => setShowIos(false)}
        >
          <div
            className="relative w-full max-w-sm bg-background border border-border rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowIos(false)}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <p className="editorial-kicker text-foreground/50">{c.label}</p>
            <h3 className="font-serif-display text-2xl mt-1 leading-tight">{c.ios.title}</h3>
            <p className="text-sm text-foreground/70 mt-3">{c.ios.body}</p>

            <div className="mt-5 flex items-center gap-3 rounded-lg border border-border p-3">
              <div className="w-10 h-10 rounded-md bg-foreground/5 flex items-center justify-center shrink-0">
                <Share className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-medium">1 · {c.ios.step1}</p>
                <p className="text-[12px] text-foreground/60">{c.ios.step1sub}</p>
              </div>
            </div>
            <div className="flex justify-center py-1 text-foreground/30" aria-hidden="true">↓</div>
            <div className="flex items-center gap-3 rounded-lg border border-border p-3">
              <div className="w-10 h-10 rounded-md bg-foreground/5 flex items-center justify-center shrink-0">
                <PlusSquare className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-medium">2 · {c.ios.step2}</p>
                <p className="text-[12px] text-foreground/60">{c.ios.step2sub}</p>
              </div>
            </div>

            <div className="mt-6 text-right">
              <button
                type="button"
                onClick={() => {
                  setInstallDecision("later");
                  setShowIos(false);
                }}
                className="h-10 px-5 rounded-lg bg-foreground text-background text-[12px] tracking-[0.14em] uppercase hover:bg-foreground/90"
                data-testid="guest-install-ios-close"
              >
                {c.ios.gotIt}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GuestInstallChip;
